import React, { useState } from 'react';
import ManageRooms from './Dashboard/Role-based/ManageRooms';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaCalendarAlt, FaUser, FaReceipt, FaEye, FaCheck, FaTimes } from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const Reschedule = () => {
  // Retrieve reservation data from session storage
  const reservation = JSON.parse(sessionStorage.getItem('selectedReservation'));

  const [formData, setFormData] = useState({
    fullName: `${reservation?.firstname || ''} ${reservation?.lastname || ''}`,
    bookingId: reservation?.bookingid || '',
    totalGuests: reservation?.numberofguests || '',
    action: 'reschedule',
    checkIn: new Date(reservation?.checkindate) || new Date(),
    checkOut: reservation?.checkoutdate ? new Date(reservation.checkoutdate) : null,
    additionalCharges: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckOutChange = (date) => {
    setFormData({ ...formData, checkOut: date });
  };

  const handleUpdateDates = () => {
    if (!formData.checkOut) {
      toast.error('Please select a check-out date');
      return;
    }
    setIsModalOpen(true);
  };

  const handleConfirm = async () => {
    setIsModalOpen(false);
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/reschedule-frontdesk/?booking_id=${
          formData.bookingId
        }&new_checkin_date=${formData.checkIn.toISOString().split('T')[0]}&new_checkout_date=${
          formData.checkOut.toISOString().split('T')[0]
        }&cancellation_fee=${formData.additionalCharges}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.error) {
        toast.error(data.details);
      } else if (response.ok) {
        toast.success(data.message);
        // Clear form after successful reschedule
        setFormData((prev) => ({
          ...prev,
          checkOut: null,
          additionalCharges: 0,
        }));
      } else {
        toast.error('Failed to reschedule booking');
      }
    } catch (error) {
      toast.error('This booking has already been rescheduled and cannot be rescheduled again.');
    }
  };

  const handlePreview = async () => {
    if (!formData.checkOut) {
      toast.error('Please select a check-out date first');
      return;
    }

    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/reschedule-frontdesk/preview/?booking_id=${
          formData.bookingId
        }&new_checkin_date=${formData.checkIn.toISOString().split('T')[0]}&new_checkout_date=${
          formData.checkOut.toISOString().split('T')[0]
        }&cancellation_fee=${formData.additionalCharges}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setPreviewData(data);
        setIsPreviewModalOpen(true);
      } else {
        toast.error(data.error || 'Failed to fetch preview data');
      }
    } catch (error) {
      toast.error('Network error while fetching preview data');
    }
  };

  return (
    <div className="p-6 bg-white min-h-screen">
      <ManageRooms />

      {/* Header Section */}
      <header className="text-center mb-8 mt-6">
        <div className="flex items-center justify-center space-x-3 mb-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <FaCalendarAlt className="text-2xl text-gray-700" />
          </div>
          <h1 className="text-3xl font-semibold text-gray-900">Reschedule Booking</h1>
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Update check-out dates and review pricing changes before confirming the reschedule.
        </p>
        <div className="w-24 h-1 bg-gray-300 mx-auto mt-4 rounded-full"></div>
      </header>

      {/* Main Form Section */}
      <div className="max-w-6xl mx-auto bg-gray-50 rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Guest Information */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
              <FaUser className="text-gray-500" />
              <span>Guest Name</span>
            </label>
            <input
              name="fullName"
              placeholder="Enter guest full name"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all duration-200 bg-white"
            />
          </div>

          {/* Booking ID */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">Reservation number</label>
            <input
              name="bookingId"
              placeholder="Enter booking ID"
              value={formData.bookingId}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all duration-200 bg-white"
            />
          </div>

          {/* Total Guests */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">Number of Guests</label>
            <input
              name="totalGuests"
              placeholder="Enter number of guests"
              type="number"
              value={formData.totalGuests}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all duration-200 bg-white"
            />
          </div>

          {/* Check-In Date */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">
              Current Check-In Date
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.checkIn.toLocaleDateString('en-US', {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 font-medium cursor-not-allowed"
              />
            </div>
          </div>

          {/* Check-Out Date */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2">New Check-Out Date</label>
            <DatePicker
              selected={formData.checkOut}
              onChange={handleCheckOutChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all duration-200 bg-white"
              placeholderText="Select new check-out date"
              dateFormat="MMM dd, yyyy"
              minDate={new Date(formData.checkIn)}
              popperPlacement="bottom-start"
            />
          </div>

          {/* Additional Charges */}
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center space-x-2">
              <FaReceipt className="text-gray-500" />
              <span>Additional Charges</span>
            </label>
            <input
              name="additionalCharges"
              placeholder="Enter additional charges"
              type="number"
              value={formData.additionalCharges}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all duration-200 bg-white"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handlePreview}
            className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-800 text-gray-800 font-semibold rounded-lg hover:bg-gray-100 hover:text-gray-800 transition-all duration-200 min-w-[140px]"
          >
            <FaEye />
            <span>Preview Changes</span>
          </button>
          <button
            onClick={handleUpdateDates}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200 min-w-[140px]"
          >
            <FaCheck />
            <span>Update Booking</span>
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FaCalendarAlt className="text-xl text-gray-700" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Confirm Date Changes</h2>
            </div>

            <p className="text-gray-600 mb-2">You are about to update the booking dates:</p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">New Check-out:</span>
                <span className="font-semibold text-gray-900">
                  {formData.checkOut?.toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Additional Charges:</span>
                <span className="font-semibold text-gray-900">
                  ₹{formData.additionalCharges || 0}
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex items-center space-x-2 px-4 py-2.5 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200"
              >
                <FaTimes />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleConfirm}
                className="flex items-center space-x-2 px-4 py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200"
              >
                <FaCheck />
                <span>Confirm Update</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {isPreviewModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <FaReceipt className="text-xl text-gray-700" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">Reschedule Cost Preview</h2>
              </div>
            </div>

            {previewData && (
              <div className="space-y-6">
                {/* Booking Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Reservation number</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {previewData.booking_id}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Total Stay Duration</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {previewData.total_days_stay} days
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-600">New Check-in</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {previewData.new_checkin_date}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600">New Check-out</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {previewData.new_checkout_date}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">Base Amount</span>
                      <span className="font-semibold text-gray-900">
                        ₹{previewData.base_amount}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">Tax Amount</span>
                      <span className="font-semibold text-gray-900">₹{previewData.tax_amount}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-t border-gray-200 pt-3">
                      <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                      <span className="text-lg font-semibold text-gray-900">
                        ₹{previewData.final_amount_with_tax}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => setIsPreviewModalOpen(false)}
                className="px-6 py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Reschedule;
